import { Modal, ModalClose, ModalContent, ModalFooter, ModalHeader, useBCMSModal } from "@bcm-dev/bcms-vite";
import { UICancelButton, UIPrimaryButton, UISecondaryButton } from "components/ui/UIButton";
import React, { useState } from "react";
import { SVGAsset } from "res";
import "./styles.css";

const CustomModalContext = React.createContext(null);

/**
 * @function CustomModal
 * @description Main modal component, wraps all modal content
 * @param {STRING} title - The title of the modal
 * @param {STRING} id - The id of the modal
 * @returns {React.Component}
 * @example
 * function TestModal(props) {
 *  return (
 *      <CustomModal id="TestModal" title="Testing">
 *          <CustomModalContent>
 *          </CustomModalContent>
 * 
 *          <CustomModalFooter>
 *              <ModalCloseButton id="TestModal" />
 *              <ModalPrimaryButton text="Continue" />
 *          </CustomModalFooter>
 *      </CustomModal>
 *  );
 * }
 */
export default function CustomModal(props) {
    const className = props.className ?? "";
    const id = props.id ?? "";

    const [ titleState, setTitleState ] = useState(props.title ?? "[Modal Title]");

    return (
        <CustomModalContext.Provider value={{ titleState, setTitleState }}>
            <Modal className={ `custom-modal ${ className }` }>
                <ModalHeader className="flex-container flex-horizontal">
                    <h3 className="flex-width text-center">{ titleState }</h3>     
                </ModalHeader>
                { props.children }
            </Modal>
        </CustomModalContext.Provider>
    );
}

export const useCustomModal = () => React.useContext(CustomModalContext);

/**
 * @function CustomModalContent
 * @description Modal Content component
 * @param {STRING} className - Additional class that will be added
 * @returns {React.Component}
 * @example
 * <CustomModalContent className="SomeClass">
 *  ...
 * </CustomModalContent>
 */
export function CustomModalContent(props) {
    return (
        <ModalContent>
            { props.children }
        </ModalContent>
    );
}

/**
 * @function CustomModalFooter
 * @description Modal footer component
 * @param {STRING} className - Additional class that will be added
 * @returns {React.Component}
 * @example
 * <CustomModalFooter className="SomeClass">
 *  ...
 * </CustomModalFooter>
 */
export function CustomModalFooter(props) {
    const className = props.className ?? "";

    return (
        <ModalFooter className={`${className} flex-container flex-vertical align-center-container`}>
            { props.children }
        </ModalFooter>
    );
}

/**
 * @function ModalSecondaryButton
 * @param {STRING} text - Allows us to set the button text.
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @returns {React.Component}
 * @example
 * <ModalSecondaryButton text="Some Text" onClick={ () => undefined } />
 */
export function ModalSecondaryButton(props) {
    const text = props.text ?? "";
    const onClick = props.onClick ?? null;

    return <UICancelButton text={ text } onClick={ onClick } />
}

/**
 * @function ModalPrimaryButton
 * @param {STRING} text - Allows us to set the button text.
 * @param {FUNCTION} onClick - Allows us to set an event handler for the button when clicked.
 * @param {STRING} dataPoint - Data point that should be applied
 * @returns {React.Component}
 * @example
 * <ModalPrimaryButton text="Some Text" onClick={ () => undefined } />
 */
export function ModalPrimaryButton(props) {
    const text = props.text ?? "";
    const { close } = useBCMSModal();
    const onClick = ()=>{   
       if( props.close === true){
        close(props.id)
        if(props.onClick) { props.onClick()};
       }else{
        if(props.onClick) { props.onClick()};
       } 
       
    }
    const point = props.dataPoint ?? "";

    return <UIPrimaryButton text={ text } onClick={ onClick } dataPoint={ point } />
}

/**
 * @function ModalCloseButton
 * @param {STRING} id - The id of the modal that should be closed
 * @returns {React.Component}
 * @example
 * <ModalCloseButton id="TestModal" />
 */
export function ModalCloseButton(props) {
    const { close } = useBCMSModal();
    return <UISecondaryButton text="Cancel" onClick={ () => close(props.id) } />;
}